import React, { FC, useContext, useEffect, useReducer, useState } from "react";
import moment from "moment";
import { QESAlert, QESButton, QESCheckbox, QESSelect, QESTextArea } from "@app/shared/qes-standards";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import ExceptionsService from "@app/exceptions/exceptionsService";
import SpecialtyGroupExceptionsService from "@app/projects/specialtyGroupExceptionsService";
import { BulkExceptionEditModel, EditExceptionModel, ExceptionDetailsModel } from "@app/exceptions/interfaces";
import { SpecialtyGroupExceptionStatus, UpdateSpecialtyGroupException } from "@app/projects/results/common";
import { DropdownItem } from "@app/shared/checkbox-dropdown";
import { toast } from "react-toastify";
import DragAndDropMultiFileUploader from "@app/shared/DragAndDrogMultiFileUploader";
import {
  SpecialtyGroupExceptionGrade,
  SupplementaryDocument
} from "@app/projects/results/common/specialtyGroupException";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import {
  DragAndDropMultiFileUploaderContext,
  DragAndDropMultiFileUploaderContextProvider
} from "@app/shared/DragAndDropMultiFileUploaderContext";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import { ExceptionsFiltersContext } from "@app/exceptions/contexts/ExceptionsFiltersContext";
import BulkEditConfirmationModal from "@app/exceptions/BulkEditConfirmationModal";
import { Roles, UserContext } from "@app/user";
import { TooltipWrapper } from "@app/shared";
import {
  EditExceptionForm,
  EditExceptionFormActionType,
  editExceptionFormReducer,
  hasChanges,
  hasValidFields,
  normalizeSpaces
} from "@app/exceptions/editExceptionReducer";

type EditExceptionFlyoutProps = {
  editException?: EditExceptionModel;
  exceptionStatusOptions: DropdownItem<string>[];
  isForArchived?: boolean;
  isForBulkEdit?: boolean;
  refreshData?: () => void;
}

const editExceptionFlyoutFormInitialState: EditExceptionForm = {
  comment: '',
  endDate: '',
  grade: SpecialtyGroupExceptionGrade.None,
  narrative: '',
  originalComment: '',
  originalEndDate: '',
  originalGrade: SpecialtyGroupExceptionGrade.None,
  originalNarrative: '',
  originalPreviouslyApproved: false,
  originalStartDate: '',
  originalStatus: SpecialtyGroupExceptionStatus.None,
  originalUploadedDocuments: [],
  previouslyApproved: false,
  startDate: '',
  status: SpecialtyGroupExceptionStatus.None,
  uploadedDocuments: [],
  validEndDate: true,
  validStartDate: true,
}

const WrappedEditExceptionFlyout: FC<React.PropsWithChildren<EditExceptionFlyoutProps>> = (props: EditExceptionFlyoutProps) => {
  const flyoutContext = useContext(FlyoutContext);
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const multiFileUploaderContext = useContext(DragAndDropMultiFileUploaderContext)
  const userContext = useContext(UserContext);
  const exceptionsService = new ExceptionsService();
  const specialtyGroupExceptionsService = new SpecialtyGroupExceptionsService();
  const uploadDocumentsApiKey: string = "document";
  const exceptionFilterContext = useContext(ExceptionsFiltersContext);
  const [isBulkEditConfirmationModalOpen, setIsBulkEditConfirmationModalOpen] = useState(false);

  const gradeContainerClasses = "exceptions__grade-control-container mt-3 p-0";
  const disableGradeEdits = !userContext.roles.includes(Roles.SPECIALTY_EXCEPTION_REVIEWER_ADMIN);

  const [formState, formStateDispatch] = useReducer(editExceptionFormReducer, editExceptionFlyoutFormInitialState);

  const isSaveEnabled = () => {
    if (props.isForBulkEdit) {
      return formState.originalStatus !== formState.status && formState.validStartDate && formState.validEndDate;
    }
    return hasChanges(formState) && hasValidFields(formState) && (!props.isForArchived || formState.originalStatus !== formState.status);
  }

  const getExceptionDetails = async () => {
    const { customProjectId, exceptionId } = props.editException;
    const details: ExceptionDetailsModel = await exceptionsService.getExceptionDetails(customProjectId, exceptionId);
    details.supplementaryDocuments.forEach(item => {
      multiFileUploaderContext.setUploadedFiles(prevFiles => new Map(prevFiles).set(item.supplementaryDocumentMetadataId, new File([], item.documentName)));
    });
    const originalStartDate = props.editException?.startDate !== '---' && !props.isForBulkEdit ?
      moment(props.editException?.startDate).format("YYYY-MM-DD") :
      '';
    const originalEndDate = props.editException?.endDate !== '---' && !props.isForBulkEdit ?
      moment(props.editException?.endDate).format("YYYY-MM-DD") :
      '';
    const originalPreviouslyApproved = props.editException?.previouslyApproved != undefined && !props.isForBulkEdit ?
      props.editException?.previouslyApproved : false;

    formStateDispatch({
      payload: {
        comment: details.comment,
        endDate: originalEndDate,
        grade: details.grade,
        narrative: details.exceptionNarrative,
        previouslyApproved: originalPreviouslyApproved,
        startDate: originalStartDate,
        status: props.isForArchived ? SpecialtyGroupExceptionStatus.None : props.editException.status,
        uploadedDocuments: details.supplementaryDocuments,
      },
      type: EditExceptionFormActionType.Reset
    });
  };

  useEffect(() => {
    if (props.isForBulkEdit) {
      formStateDispatch({ payload: SpecialtyGroupExceptionStatus.None, type: EditExceptionFormActionType.ResetStatus})
    } else {
      getExceptionDetails();
    }
  }, []);

  const handleSave = async () => {
    try {
      if (props.isForBulkEdit) {
        setIsBulkEditConfirmationModalOpen(true);
      } else {
        const updateException: UpdateSpecialtyGroupException = {
          comment: formState.comment,
          endDate: formState.endDate ? moment(formState.endDate).toDate() : undefined,
          exceptionNarrative: formState.narrative,
          grade: formState.grade,
          previouslyApproved: formState.previouslyApproved,
          specialtyGroupExceptionId: props.editException.exceptionId,
          specialtyGroupExceptionStatus: formState.status,
          startDate: formState.startDate ? moment(formState.startDate).toDate() : undefined,
          supplementaryDocuments: formState.uploadedDocuments
        };

        await specialtyGroupExceptionsService.updateSpecialtyGroupException(props.editException.customProjectId, updateException);

        flyoutContext.handleCloseFlyout();
        props.refreshData();

        toast("Exception Status successfully updated!",
          {
            containerId: "z-index-9999",
            type: "success"
          }
        );

        if (props.isForArchived) {
          exceptionFilterContext.reloadFilters();
        }
      }
    } catch (e) {
      toast("Exception Status failed to update!",
        {
          containerId: "z-index-9999",
          type: "error"
        }
      );
    }
  }

  const handleBulkSave = async () => {
    try {
      const bulkExceptionEditModel: BulkExceptionEditModel = {
        comment: formState.comment,
        endDate: formState.endDate ? moment(formState.endDate).toDate() : undefined,
        exceptionNarrative: formState.narrative,
        grade: formState.grade,
        previouslyApproved: formState.previouslyApproved,
        selectedExceptionIds: exceptionFilterContext.selectedExceptionIds,
        specialtyGroupExceptionStatus: formState.status,
        startDate: formState.startDate ? moment(formState.startDate).toDate() : undefined
      };

      await exceptionsService.bulkUpdateExceptionsByIds(bulkExceptionEditModel);

      setIsBulkEditConfirmationModalOpen(true);

      toast(`The selected exception${exceptionFilterContext.selectedExceptionIds.length == 1 ? "" : "s"} ${exceptionFilterContext.selectedExceptionIds.length == 1 ? "has" : "have"} been successfully updated!`,
        {
          containerId: "z-index-9999",
          type: "success"
        }
      );
    } catch (e) {
      toast(`The selected exception${exceptionFilterContext.selectedExceptionIds.length == 1 ? "" : "s"} failed to update!`,
        {
          containerId: "z-index-9999",
          type: "error"
        }
      );
    } finally {
      exceptionFilterContext.setSelectedExceptionIds([]);
      exceptionFilterContext.setTriggerRefreshData(true);
      flyoutContext.handleCloseFlyout();
    }
  }

  const handleDownload = async (metadataId: number) => {
    const link = (!!metadataId && metadataId > 0) ?
      `${exceptionsService.downloadSupplementaryDocument(metadataId)}` : `#`;
    authenticatedDownload(link);
  };

  const handleFileUpload = async (file: File) => {

    return new Promise<void>(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append(uploadDocumentsApiKey, file);
        const metadataId = await exceptionsService.uploadSupplementaryDocument(formData);
        const fileinfo: FormDataEntryValue = formData.get(uploadDocumentsApiKey);
        if (!!metadataId && fileinfo instanceof File) {
          const supplementaryDocument: SupplementaryDocument = {
            documentName: fileinfo.name ? fileinfo.name : '',
            supplementaryDocumentMetadataId: metadataId
          }
          formStateDispatch({ payload: [supplementaryDocument], type: EditExceptionFormActionType.AddUploadedDocuments })
          multiFileUploaderContext.setUploadedFiles(prevFiles => new Map(prevFiles).set(metadataId, file));
          resolve();
        }
      } catch (error) {
        reject(error.responseJSON);
      }

    });
  };

  return (
    <>
      <div className="row">

        <div className="col-10 mb-3">
          <QESAlert type="info" persist={true}>
            Editing information for selected exceptions will overwrite ALL previously entered information.
          </QESAlert>

          {!props.isForBulkEdit &&
            <>
              <div className="mt-3 font-weight-bold">{`Service Area: ${props.editException.serviceArea}`}</div>
              <div className="mt-0 mb-4 font-weight-bold">{`Specialty: ${props.editException.specialty}`}</div>
            </>
          }

          <QESSelect
            id={"edit-exception-flyout__status-dropdown"}
            className="col-5"
            label="Status this year"
            onChange={(e) => formStateDispatch({
              payload: e.target.value as SpecialtyGroupExceptionStatus,
              type: EditExceptionFormActionType.Status
            })}
            testId="edit-exception-flyout__status-dropdown"
            value={formState.status}
          >
            {props.exceptionStatusOptions.map(option => (
              <option key={option.key} value={option.key}>
                {option.name == 'None' ? '' : option.name}
              </option>
            ))};
          </QESSelect>

          <QESDateInput
            containerClassName="mt-3 col-5 p-0"
            errorText="Start Date must be before Expiration Date"
            isValid={formState.validStartDate}
            label="Start Date"
            testId="edit-exception-flyout__start-date"
            onChange={(e) => formStateDispatch({ payload: e.target.value, type: EditExceptionFormActionType.StartDate })}
            unrequiredValidation={true}
            value={formState.startDate}
          />

          <QESDateInput
            containerClassName="mt-3 col-5 p-0"
            errorText="Expiration Date must be after Start Date"
            isValid={formState.validEndDate}
            label="Expiration Date"
            testId="edit-exception-flyout__end-date"
            onChange={(e) => formStateDispatch({ payload: e.target.value, type: EditExceptionFormActionType.EndDate })}
            unrequiredValidation={true}
            value={formState.endDate}
          />

          <div className="col-5 p-0">
            <TooltipWrapper
              text={disableGradeEdits ? 'Users with sufficient permissions may edit the Exception Grade.' : null}
              containerClassName={disableGradeEdits ? gradeContainerClasses : ''}
              triggerWidth="100%">
              <div
                className={disableGradeEdits ? '' : gradeContainerClasses}>
                <TooltipWrapper
                  text={`A = Exception has been reviewed, has all necessary documentation attached and is ready for submission.
B = Exception needs some work around the narrative and/or reviewing additional providers before submission.\n
C = Exception is missing necessary supporting documentation, narrative, and/or excluded providers.`}>
                  <i className="fs14 fa fa-info-circle"/>
                </TooltipWrapper>
                <QESSelect
                  className="form-control"
                  value={formState.grade}
                  onChange={e => formStateDispatch({
                    payload: e.target.value as SpecialtyGroupExceptionGrade,
                    type: EditExceptionFormActionType.Grade
                  })}
                  label="Exception Grade"
                  testId="edit-exception-flyout__grade-dropdown"
                  disabled={disableGradeEdits}
                >
                  {Object.keys(SpecialtyGroupExceptionGrade).map((grade) => {
                    const value: SpecialtyGroupExceptionGrade = (SpecialtyGroupExceptionGrade)[grade as keyof typeof SpecialtyGroupExceptionGrade];
                    if (value === SpecialtyGroupExceptionGrade.None) {
                      return (
                        <option key={value} value={value}>---</option>
                      );
                    }
                    return (
                      <option key={value} value={value}>{grade}</option>
                    );
                  })}
                </QESSelect>
              </div>
            </TooltipWrapper>
          </div>

          <QESCheckbox
            containerClassName="mt-3"
            label="Previously Approved"
            checked={formState.previouslyApproved}
            onChange={(e) => formStateDispatch({
              payload: (e.target as HTMLInputElement).checked,
              type: EditExceptionFormActionType.PreviouslyApproved
            })}
            id="edit-exception-flyout__previously-approved"
            testId="edit-exception-flyout__previously-approved"
          />

          <QESTextArea
            id={"edit-exception-flyout__comment-input"}
            containerClassName="mt-3"
            rows={4}
            value={formState.comment}
            onChange={(e) => formStateDispatch({ payload: e.target.value, type: EditExceptionFormActionType.Comment })}
            onBlur={() => formStateDispatch({ payload: normalizeSpaces(formState.comment), type: EditExceptionFormActionType.Comment })}
            maxLength={4000}
            label="Comments"
            testId="edit-exception-flyout__comment-input"
          />

          <QESTextArea
            id={"edit-exception-flyout__narrative-input"}
            containerClassName="mt-3"
            rows={8}
            value={formState.narrative}
            onChange={(e) => formStateDispatch({ payload: e.target.value, type: EditExceptionFormActionType.Narrative})}
            onBlur={() => formStateDispatch({ payload: normalizeSpaces(formState.narrative), type: EditExceptionFormActionType.Narrative})}
            maxLength={4000}
            label="Exception Narrative"
            testId="edit-exception-flyout__narrative-input"
          />

          {featureFlags.has(Features.VOLUME_EXCEPTIONS_SUPPLEMENTARY_DOCUMENTS) && !props.isForBulkEdit &&
            <DragAndDropMultiFileUploader
              apiKeyName={uploadDocumentsApiKey}
              title="Attach Supplemental Documents. Attachments must be in .doc, .docx, .png, .jpg, or .pdf format."
              filetype=".doc,.docx,.png,.jpg,.jpeg,.pdf"
              onMultipleUpload={handleFileUpload}
              onRemove={key => formStateDispatch({ payload: key, type: EditExceptionFormActionType.RemoveUploadedDocumentByKey })}
              handleDownloadLink={handleDownload}
              small={true}
              testId="volume-exception-edit-upload-supplementary-docs"
            />
          }

        </div>
      </div>

      <FlyoutFooter
        submitButton={
          <QESButton
            qesType="primary"
            qesTestId="gap-fill-status-status-flyout__save"
            disabled={!isSaveEnabled()}
            onClick={() => {
              handleSave().then();
            }}
          >
            Save
          </QESButton>}
      />

      <BulkEditConfirmationModal
        isModalOpen={isBulkEditConfirmationModalOpen}
        setIsModalOpen={setIsBulkEditConfirmationModalOpen}
        handleConfirm={handleBulkSave}
      />

    </>
  );
}

const EditExceptionFlyout: FC<React.PropsWithChildren<EditExceptionFlyoutProps>> = (props: EditExceptionFlyoutProps) => (
  <DragAndDropMultiFileUploaderContextProvider>
    <WrappedEditExceptionFlyout {...props} />
  </DragAndDropMultiFileUploaderContextProvider>
);

export default EditExceptionFlyout;
